import { withStyleNonce } from '@/lib/csp-nonce';
import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import {
  CAMPAIGN_PREVIEW_SAMPLE_CONTACT,
  extractUnresolvedCampaignMergeTags,
  substituteCampaignMergeTags,
} from '../../utils/campaign-content-preview.utils';
import { cn } from '@/lib/utils';

type CampaignContentPreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  html: string;
  plainText?: string;
};

export const CampaignContentPreviewDialog = ({
  open,
  onOpenChange,
  html,
  plainText = '',
}: CampaignContentPreviewDialogProps) => {
  const [tab, setTab] = useState('desktop');

  const rendered = useMemo(() => substituteCampaignMergeTags(html), [html]);
  const unresolved = useMemo(() => extractUnresolvedCampaignMergeTags(html), [html]);
  const sampleName = `${CAMPAIGN_PREVIEW_SAMPLE_CONTACT.first_name} ${CAMPAIGN_PREVIEW_SAMPLE_CONTACT.last_name}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setTab('desktop');
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-[640px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-3 h-auto w-full justify-start gap-1 rounded-none border-b border-border bg-transparent p-0">
            {(['desktop', 'mobile', 'plain'] as const).map((key) => (
              <TabsTrigger
                key={key}
                value={key}
                className={cn(
                  'rounded-none border-b-2 border-transparent px-3 py-2 shadow-none data-[state=active]:border-primary-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                )}
              >
                {key === 'desktop' ? 'Desktop' : key === 'mobile' ? 'Mobile' : 'Plain text'}
              </TabsTrigger>
            ))}
          </TabsList>

          {unresolved.length > 0 ? (
            <p className="mb-3 rounded-lg bg-destructive/10 px-3 py-2.5 text-xs text-destructive">
              Unresolved merge tag(s): {unresolved.map((token) => `{{${token}}}`).join(', ')}
            </p>
          ) : null}

          <p className="mb-2 text-xs text-muted-foreground">
            Merge tags resolve against a synthetic sample contact — “{sampleName}”, not a real
            recipient.
          </p>

          <TabsContent value="desktop" className="mt-0">
            <iframe
              title="Desktop email preview"
              srcDoc={withStyleNonce(rendered)}
              className="h-[360px] w-full rounded-lg border border-border bg-white"
              sandbox="allow-same-origin"
            />
          </TabsContent>
          <TabsContent value="mobile" className="mt-0 flex justify-center">
            <iframe
              title="Mobile email preview"
              srcDoc={withStyleNonce(rendered)}
              className="h-[420px] w-full max-w-[375px] rounded-lg border border-border bg-white"
              sandbox="allow-same-origin"
            />
          </TabsContent>
          <TabsContent value="plain" className="mt-0">
            {plainText.trim() ? (
              <pre className="whitespace-pre-wrap text-xs font-[inherit] text-foreground">
                {plainText}
              </pre>
            ) : (
              <p className="text-xs text-muted-foreground">
                No saved plain-text version yet — save your content once to generate one. This tab
                always shows the last saved version, not your unsaved edits.
              </p>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
